import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { palette } from '@m12s/component-library';

import MainLayout from 'components/Layouts/MainLayout';
import { AppNav } from 'components/AppNav';
import { HeaderLayout, HeaderText } from 'components/AppNav/styled';
import {
  clampPickQty,
  formatMaterialQty,
  formatMaterialRatio,
} from 'lib/utils/pickingMaterials';

const Scroll = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${palette.Grey100};
  padding: 1.5rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 1.5rem;

  & + & {
    padding-top: 1.5rem;
    border-top: 1px solid ${palette.Grey300};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const MaterialLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${palette.Grey700};
`;

const TotalLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ over }) => {
    return over ? palette.Red600 : palette.Grey500;
  }};
  white-space: nowrap;
`;

const LpRow = styled.div`
  display: flex;
  align-items: center;
  background: ${palette.White};
  border-radius: 0.5rem;
  border: 1px solid ${palette.Grey300};
  overflow: hidden;
  height: 3rem;
`;

const LpLabel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.0625rem;
  padding: 0 1rem;
  overflow: hidden;
`;

const LpId = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${palette.Grey800};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LpLoc = styled.span`
  font-size: 0.75rem;
  color: ${palette.Grey500};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QtyArea = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0 0.75rem;
  border-left: 1px solid ${palette.Grey300};
  height: 100%;
`;

const QtyInput = styled.input`
  width: 3.5rem;
  height: 1.75rem;
  border: 1px solid ${palette.Grey300};
  border-radius: 0.25rem;
  text-align: center;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${palette.Grey800};
  background: ${palette.White};
  outline: none;
  padding: 0 0.25rem;

  &:focus {
    border-color: ${palette.Indigo400};
    box-shadow: 0 0 0 2px ${palette.Indigo100};
  }
`;

const QtyMax = styled.span`
  font-size: 0.8125rem;
  color: ${palette.Grey500};
  white-space: nowrap;
`;

const Stepper = styled.button`
  width: 3rem;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-left: 1px solid ${palette.Grey300};
  background: ${palette.White};
  font-size: 1.125rem;
  font-weight: 400;
  color: ${({ disabled }) => {
    return disabled ? palette.Grey300 : palette.Grey700;
  }};
  cursor: ${({ disabled }) => {
    return disabled ? 'not-allowed' : 'pointer';
  }};

  &:hover:not(:disabled) {
    background: ${palette.Grey100};
  }

  &:active:not(:disabled) {
    background: ${palette.Grey200};
  }
`;

const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  border-top: 1px solid ${palette.Grey300};
  background: ${palette.White};
`;

const FooterBtn = styled.button`
  flex: 1;
  height: 3rem;
  border: none;
  background: none;
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${({ disabled }) => {
    return disabled ? palette.Grey400 : palette.Grey700;
  }};
  cursor: ${({ disabled }) => {
    return disabled ? 'not-allowed' : 'pointer';
  }};

  &:not(:first-child) {
    border-left: 1px solid ${palette.Grey300};
  }

  &:last-child {
    font-weight: 600;
    color: ${({ disabled, submitting }) => {
      if (disabled) return palette.Grey400;
      if (submitting) return palette.Grey500;
      return palette.Indigo600;
    }};
  }

  &:hover:not(:disabled) {
    background: ${palette.Grey50};
  }
`;

// Manages local input string state so partial typing (e.g. "") doesn't
// immediately clamp to 0 mid-keystroke. Commits to the parent on blur/enter.
const QtyCell = ({ qty, maxQty, unit, itemNumber, lp, onAdjust }) => {
  const [draft, setDraft] = useState(String(qty));

  useEffect(() => {
    setDraft(String(qty));
  }, [qty]);

  const commit = useCallback(
    (raw) => {
      const clamped = clampPickQty(raw, maxQty);
      setDraft(String(clamped));
      onAdjust(itemNumber, lp, clamped);
    },
    [itemNumber, lp, maxQty, onAdjust]
  );

  return (
    <QtyArea>
      <QtyInput
        type="text"
        inputMode="decimal"
        aria-label={`Quantity from ${lp} for ${itemNumber}, up to ${maxQty} ${unit}`}
        value={draft}
        onChange={(e) => {
          return setDraft(e.target.value);
        }}
        onBlur={(e) => {
          return commit(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.target.blur();
          }
        }}
      />
      <QtyMax>
        of {maxQty} {unit}
      </QtyMax>
    </QtyArea>
  );
};

QtyCell.propTypes = {
  qty: PropTypes.number.isRequired,
  maxQty: PropTypes.number.isRequired,
  unit: PropTypes.string.isRequired,
  itemNumber: PropTypes.string.isRequired,
  lp: PropTypes.string.isRequired,
  onAdjust: PropTypes.func.isRequired,
};

const ConfirmQuantitiesStep = ({
  materials,
  selections,
  quantities,
  onAdjust,
  onReset,
  onSubmit,
  onBack,
  mode,
  submitting,
  canSubmit,
}) => {
  const confirmLabel = mode === 'start-job' ? 'Start job' : 'Confirm materials';

  return (
    <MainLayout.Column id="confirm-quantities-step">
      <AppNav onBack={onBack}>
        <HeaderLayout>
          <HeaderText semibold color={palette.White}>
            Confirm material quantities
          </HeaderText>
        </HeaderLayout>
      </AppNav>

      <MainLayout.Container direction="column" bgColor={palette.Grey100}>
        <Scroll>
          {materials.map((mat) => {
            const sel = selections[mat.itemNumber] || new Set();
            const selectedLps = mat.lps.filter((lp) => {
              return sel.has(lp.lp);
            });
            if (selectedLps.length === 0) return null;

            const totalSelected = selectedLps.reduce((sum, lp) => {
              const key = `${mat.itemNumber}:${lp.lp}`;
              return sum + (quantities[key] ?? 0);
            }, 0);
            const isOver = totalSelected > mat.requiredQty;
            const ratio = formatMaterialRatio(mat.qtyPerUnit);

            return (
              <Section key={mat.itemNumber}>
                <SectionHeader>
                  <MaterialLabel>
                    {mat.itemNumber}
                    {ratio ? ` | ${ratio}` : ''}
                  </MaterialLabel>
                  <TotalLabel over={isOver}>
                    {formatMaterialQty(totalSelected)} of {mat.requiredQty}{' '}
                    required
                  </TotalLabel>
                </SectionHeader>

                {selectedLps.map((lp) => {
                  const key = `${mat.itemNumber}:${lp.lp}`;
                  const qty = quantities[key] ?? 0;
                  return (
                    <LpRow key={lp.lp}>
                      <LpLabel>
                        <LpId>{lp.lp}</LpId>
                        {lp.location ? <LpLoc>{lp.location}</LpLoc> : null}
                      </LpLabel>
                      <QtyCell
                        qty={qty}
                        maxQty={lp.available ?? lp.onHand}
                        unit={mat.unit}
                        itemNumber={mat.itemNumber}
                        lp={lp.lp}
                        onAdjust={onAdjust}
                      />
                      <Stepper
                        type="button"
                        aria-label={`Decrease quantity from ${lp.lp}`}
                        disabled={qty <= 0}
                        onClick={() => {
                          return onAdjust(
                            mat.itemNumber,
                            lp.lp,
                            clampPickQty(qty - 1, lp.available ?? lp.onHand)
                          );
                        }}
                      >
                        −
                      </Stepper>
                      <Stepper
                        type="button"
                        aria-label={`Increase quantity from ${lp.lp}`}
                        disabled={qty >= (lp.available ?? lp.onHand)}
                        onClick={() => {
                          return onAdjust(
                            mat.itemNumber,
                            lp.lp,
                            clampPickQty(qty + 1, lp.available ?? lp.onHand)
                          );
                        }}
                      >
                        +
                      </Stepper>
                    </LpRow>
                  );
                })}
              </Section>
            );
          })}
        </Scroll>
      </MainLayout.Container>

      <Footer>
        <FooterBtn type="button" onClick={onReset}>
          Reset selection
        </FooterBtn>
        <FooterBtn
          type="button"
          disabled={submitting || !canSubmit}
          submitting={submitting}
          onClick={onSubmit}
        >
          {submitting ? 'Submitting…' : confirmLabel}
        </FooterBtn>
      </Footer>
    </MainLayout.Column>
  );
};

ConfirmQuantitiesStep.propTypes = {
  materials: PropTypes.arrayOf(
    PropTypes.shape({
      itemNumber: PropTypes.string,
      requiredQty: PropTypes.number,
      qtyPerUnit: PropTypes.number,
      unit: PropTypes.string,
      lps: PropTypes.array,
    })
  ).isRequired,
  selections: PropTypes.objectOf(PropTypes.instanceOf(Set)).isRequired,
  quantities: PropTypes.objectOf(PropTypes.number).isRequired,
  onAdjust: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['start-job', 'add-materials']),
  submitting: PropTypes.bool,
  canSubmit: PropTypes.bool,
};

ConfirmQuantitiesStep.defaultProps = {
  mode: 'start-job',
  submitting: false,
  canSubmit: true,
};

export default ConfirmQuantitiesStep;
